/**
 * Common type guards for primitives and basic types
 *
 * This module provides reusable type guards for null, undefined, boolean,
 * and other common type checks throughout the codebase.
 *
 * @see docs/best-practices/typeguards-2025.md
 * @see docs/best-practices/strict-boolean-expressions-2025.md
 */

/**
 * Type guard: Checks if value is not null or undefined
 *
 * Use when: Need to filter out null/undefined from values
 * Blocks: null, undefined
 * Allows: All other values (including 0, false, empty string)
 *
 * @example
 * ```typescript
 * const values: (string | null | undefined)[] = ["a", null, "b", undefined, "c"];
 * const defined = values.filter(isDefined); // Type: string[]
 * ```
 */
export function isDefined<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined
}

/**
 * Type guard: Checks if value is null
 *
 * Use when: Need to explicitly check for null
 * Blocks: undefined, all other values
 * Allows: null only
 *
 * @example
 * ```typescript
 * if (isNull(value)) {
 *   console.log("Value is explicitly null");
 * }
 * ```
 */
export function isNull(value: unknown): value is null {
  return value === null
}

/**
 * Type guard: Checks if value is undefined
 *
 * Use when: Need to explicitly check for undefined
 * Blocks: null, all other values
 * Allows: undefined only
 *
 * @example
 * ```typescript
 * if (isUndefined(value)) {
 *   console.log("Value is undefined");
 * }
 * ```
 */
export function isUndefined(value: unknown): value is undefined {
  return value === undefined
}

/**
 * Type guard: Checks if value is null or undefined (nullish)
 *
 * Use when: Need to check if value is either null or undefined
 * Blocks: All defined values (including 0, false, empty string)
 * Allows: null, undefined
 *
 * @example
 * ```typescript
 * if (isNullish(value)) {
 *   return defaultValue;
 * }
 * ```
 */
export function isNullish(value: unknown): value is null | undefined {
  return value === null || value === undefined
}

/**
 * Type guard: Checks if value is a boolean
 *
 * Use when: Need to validate boolean type
 * Blocks: null, undefined, truthy/falsy non-booleans
 * Allows: true, false
 *
 * @example
 * ```typescript
 * function processFlag(flag: unknown): void {
 *   if (isBoolean(flag)) {
 *     toggle(flag);
 *   }
 * }
 * ```
 */
export function isBoolean(value: unknown): value is boolean {
  return typeof value === 'boolean'
}

/**
 * Type guard: Checks if value is explicitly true
 *
 * Use when: Need to check for true (not truthy values)
 * Blocks: false, null, undefined, truthy non-booleans
 * Allows: true only
 *
 * @example
 * ```typescript
 * if (isTrue(value)) {
 *   enableFeature();
 * }
 * ```
 */
export function isTrue(value: unknown): value is true {
  return value === true
}

/**
 * Type guard: Checks if value is explicitly false
 *
 * Use when: Need to check for false (not falsy values)
 * Blocks: true, null, undefined, falsy non-booleans
 * Allows: false only
 *
 * @example
 * ```typescript
 * if (isFalse(value)) {
 *   disableFeature();
 * }
 * ```
 */
export function isFalse(value: unknown): value is false {
  return value === false
}

/**
 * Type guard: Checks if value is a symbol
 *
 * Use when: Need to validate symbol type
 * Blocks: null, undefined, all non-symbol types
 * Allows: Symbols only
 *
 * @example
 * ```typescript
 * if (isSymbol(value)) {
 *   const description = value.description;
 * }
 * ```
 */
export function isSymbol(value: unknown): value is symbol {
  return typeof value === 'symbol'
}

/**
 * Type guard: Checks if value is a BigInt
 *
 * Use when: Need to validate BigInt type
 * Blocks: null, undefined, numbers, all non-bigint types
 * Allows: BigInt values only
 *
 * @example
 * ```typescript
 * if (isBigInt(value)) {
 *   const result = value * 2n;
 * }
 * ```
 */
export function isBigInt(value: unknown): value is bigint {
  return typeof value === 'bigint'
}

/**
 * Type guard: Checks if value is a function
 *
 * Use when: Need to validate callable functions
 * Blocks: null, undefined, all non-function types
 * Allows: Functions only
 *
 * @example
 * ```typescript
 * if (isFunction(callback)) {
 *   callback();
 * }
 * ```
 */
export function isFunction(value: unknown): value is (...args: unknown[]) => unknown {
  return typeof value === 'function'
}

/**
 * Type guard: Checks if value is a Date object
 *
 * Use when: Need to validate Date instances
 * Blocks: null, undefined, date strings, timestamps, invalid dates
 * Allows: Valid Date objects only
 *
 * @example
 * ```typescript
 * if (isDate(value)) {
 *   console.log(value.toISOString());
 * }
 * ```
 */
export function isDate(value: unknown): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime())
}

/**
 * Type guard: Checks if value is a RegExp object
 *
 * Use when: Need to validate RegExp instances
 * Blocks: null, undefined, strings, all non-RegExp types
 * Allows: RegExp objects only
 *
 * @example
 * ```typescript
 * if (isRegExp(value)) {
 *   const match = value.test(input);
 * }
 * ```
 */
export function isRegExp(value: unknown): value is RegExp {
  return value instanceof RegExp
}

/**
 * Type guard: Checks if value is an Error object
 *
 * Use when: Need to validate Error instances
 * Blocks: null, undefined, error-like objects, all non-Error types
 * Allows: Error objects only
 *
 * @example
 * ```typescript
 * try {
 *   riskyOperation();
 * } catch (error: unknown) {
 *   if (isError(error)) {
 *     console.error(error.message);
 *   }
 * }
 * ```
 */
export function isError(value: unknown): value is Error {
  return value instanceof Error
}
